// src/components/HazardPopup.jsx

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HazardItem from "./HazardItem";
import PropTypes from "prop-types";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";

// Define getHazardPriority function
const getHazardPriority = (hazard) => {
  const noMeasure = !hazard.measure || hazard.measure.trim() === "";
  const notControlled = hazard.controlled === false;
  const notEliminated = hazard.eliminated === false;
  return noMeasure && notControlled && notEliminated ? 0 : 1;
};

// Define getAreaPriority function
const getAreaPriority = (hazard) => {
  const areaOrder = { Internal: 0, External: 1 };
  return areaOrder.hasOwnProperty(hazard.area) ? areaOrder[hazard.area] : 2;
};

const HazardPopup = ({
  originalHazards,
  onSave,
  onApply,
  onClose,
  open,
  currentUserId,
}) => {
  const [editedHazards, setEditedHazards] = useState([]);
  const [isEditing, setIsEditing] = useState([]);
  const [saving, setSaving] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false); // New state variable

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Initialize editedHazards and isEditing when originalHazards change
  useEffect(() => {
    if (originalHazards && originalHazards.length > 0) {
      setIsEditing(originalHazards.map(() => false));
      setEditedHazards(originalHazards.map((hazard) => ({ ...hazard })));
    } else {
      setIsEditing([]);
      setEditedHazards([]);
    }
  }, [originalHazards]);

  // Sorting Logic: useMemo to memoize the sorted hazards
  const sortedHazards = useMemo(() => {
    return [...editedHazards].sort((a, b) => {
      const priorityA = getHazardPriority(a);
      const priorityB = getHazardPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const areaPriorityA = getAreaPriority(a);
      const areaPriorityB = getAreaPriority(b);

      return areaPriorityA - areaPriorityB;
    });
  }, [editedHazards]);

  // Handler to update hazard fields
  const handleInputChange = useCallback((index, field, value) => {
    setEditedHazards((prevHazards) => {
      const updatedHazards = [...prevHazards];
      if (field.includes(".")) {
        const [parent, child] = field.split(".");
        updatedHazards[index] = {
          ...updatedHazards[index],
          [parent]: {
            ...updatedHazards[index][parent],
            [child]: value,
          },
        };
      } else {
        updatedHazards[index] = { ...updatedHazards[index], [field]: value };
      }
      return updatedHazards;
    });
  }, []);

  // Handler to toggle edit mode for a specific hazard
  const handleEditToggle = useCallback((index) => {
    setIsEditing((prevEditing) => {
      const updatedEditing = [...prevEditing];
      updatedEditing[index] = !updatedEditing[index];
      return updatedEditing;
    });
  }, []);

  // Handler to remove an image from a hazard
  const handleRemoveImage = useCallback((hazardIndex, imageIndex) => {
    setEditedHazards((prevHazards) => {
      const updatedHazards = [...prevHazards];
      const updatedImages = [
        ...(updatedHazards[hazardIndex].media?.images || []),
      ];
      updatedImages.splice(imageIndex, 1);
      updatedHazards[hazardIndex].media = {
        images: updatedImages,
        permissions: [
          ...(updatedHazards[hazardIndex].media?.permissions || []),
        ],
      };
      return updatedHazards;
    });
  }, []);

  // Handler to delete a hazard
  const handleDelete = useCallback((index) => {
    setEditedHazards((prevHazards) =>
      prevHazards.filter((_, i) => i !== index),
    );
    setIsEditing((prevEditing) => prevEditing.filter((_, i) => i !== index));
  }, []);

  // Modify handleApply to open the confirmation dialog
  const handleApply = useCallback(() => {
    if (!onApply) return;

    // Open the confirmation dialog
    setOpenConfirmDialog(true);
  }, [onApply]);

  // New function to handle the actual apply after confirmation
  const handleConfirmApply = useCallback(async () => {
    setOpenConfirmDialog(false); // Close the confirmation dialog

    if (editedHazards.length === 0) {
      setApplyError("No hazards to apply.");
      return;
    }

    setSaving(true);
    setApplyError("");
    try {
      const hazardsToUpdate = editedHazards.map((hazard) => ({
        ...hazard,
        updated_by: currentUserId,
      }));

      const response = await axios.post(
        "https://formbuilderapi.getforsite.com/api/hazards/updateHazards",
        { hazards: hazardsToUpdate },
      );

      if (response.data.success) {
        setApplySuccess(true);
        onApply(hazardsToUpdate); // Notify parent component of the successful apply
        onClose(); // Close the popup
      } else {
        setApplyError("Failed to apply changes. Please try again.");
      }
    } catch (error) {
      console.error("Error applying changes:", error);
      setApplyError(
        error.response?.data?.error || "An unexpected error occurred.",
      );
    } finally {
      setSaving(false);
    }
  }, [editedHazards, currentUserId, onApply, onClose]);

  // Handler to add a new blank hazard
  const handleAddNew = () => {
    setEditedHazards((prevHazards) => [
      ...prevHazards,
      {
        hazard: "",
        area: "",
        reference: "",
        type: "",
        eliminated: false,
        location: "",
        status: 1, // default active
        frequency: null,
        initial_rating: null,
        consequence: null,
        rating: null,
        controlled: false,
        controlled_at: "",
        measure: "",
        corrective_action: "",
        internal_description: "",
        media: {
          images: [],
          permissions: [],
        },
      },
    ]);
    setIsEditing((prevEditing) => [...prevEditing, true]);
  };

  // Define risk levels and frequency levels
  const riskLevels = useMemo(
    () => [
      { label: "Low", value: 1 },
      { label: "Medium", value: 2 },
      { label: "High", value: 3 },
    ],
    [],
  );

  const frequencyLevels = useMemo(
    () => [
      { label: "Low", value: 1 },
      { label: "Medium", value: 3 },
      { label: "High", value: 5 },
    ],
    [],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        scroll="paper"
      >
        <DialogTitle>Hazard Register</DialogTitle>
        <DialogContent dividers>
          {sortedHazards && sortedHazards.length > 0 ? (
            sortedHazards.map((hazard) => {
              const index = editedHazards.findIndex((h) => h === hazard);

              return (
                <HazardItem
                  key={hazard.id || `new-hazard-${index}`}
                  hazard={hazard}
                  isEditMode={isEditing[index]}
                  onInputChange={handleInputChange}
                  onEditToggle={handleEditToggle}
                  onRemoveImage={handleRemoveImage}
                  onDelete={handleDelete}
                  index={index}
                  riskLevels={riskLevels}
                  frequencyLevels={frequencyLevels}
                  saving={saving}
                />
              );
            })
          ) : (
            <Typography>No Hazards Found for the Site.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={saving}>
            Close
          </Button>
          <Button
            onClick={handleAddNew}
            color="primary"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={saving}
          >
            Add New
          </Button>
          {onApply && (
            <Button
              onClick={handleApply}
              color="success"
              variant="contained"
              disabled={saving || editedHazards.length === 0}
              startIcon={saving && <CircularProgress size={20} />}
            >
              {saving ? "Applying..." : "Apply Changes"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Apply Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You are about to make changes to this site's live hazard register,
            are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)} color="secondary">
            No
          </Button>
          <Button
            onClick={handleConfirmApply}
            color="primary"
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={applySuccess}
        autoHideDuration={6000}
        onClose={() => setApplySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setApplySuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Changes applied successfully!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={Boolean(applyError)}
        autoHideDuration={6000}
        onClose={() => setApplyError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setApplyError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {applyError}
        </Alert>
      </Snackbar>
    </>
  );
};

HazardPopup.propTypes = {
  originalHazards: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func, // You can remove this if not needed
  onApply: PropTypes.func.isRequired, // Function to apply changes to backend
  onClose: PropTypes.func.isRequired, // Function to close the popup
  open: PropTypes.bool.isRequired, // Controls whether the popup is open
  currentUserId: PropTypes.number.isRequired, // ID of the current user making the update
};

export default React.memo(HazardPopup);
